import { IconFileUpload } from '@tabler/icons-react';
import { useFela } from 'react-fela';
import { FormattedMessage, useIntl } from 'react-intl';

import { FormField, Label, TextareaInput } from '@/modules/forms';
import type { MessageKey } from '@/modules/localizations/translations/types';
import { Button, ButtonType, IconButton, Spacer, Text } from '@/modules/ui';

import type { Fields } from '../../../constants';
import { useKeyImport } from '../../../hooks';
import * as felaRules from './SshKeyField.rules';

export interface SshKeyFieldProps {
    name: Fields.PRIVATE_KEY | Fields.PUBLIC_KEY;
    accept?: string;
    extractKey: (text: string) => string | null;
    notFoundMessageId: MessageKey;
}

export const SshKeyField = ({ name, ...importOptions }: SshKeyFieldProps) => {
    const { css } = useFela();
    const intl = useIntl();
    const { formatMessage } = intl;

    const { fileName, errorMessageId, openFilePicker, removeImport, inputProps } = useKeyImport({ name, ...importOptions });

    return (
        <div>
            <div className={css(felaRules.labelRow)}>
                <Label elementType='label' htmlFor={name}>
                    <FormattedMessage id={`secret.${name}.label`} />
                </Label>

                {fileName ? (
                    <span className={css(felaRules.file)} data-testid='dropzone-file'>
                        <Text
                            elementType='span'
                            variant='ParagraphSmallMedium'
                            color='foregroundsTertiary'
                            customStyle={felaRules.fileName}
                        >
                            {fileName}
                        </Text>
                        <IconButton type='ghost' icon='x' aria-label={formatMessage({ id: 'remove.file' })} onClick={removeImport} />
                    </span>
                ) : (
                    <Button type={ButtonType.LINK} customStyle={felaRules.importButton} onClick={openFilePicker}>
                        <IconFileUpload size={16} stroke={1.5} />
                        <FormattedMessage id='secret.sshKey.import.action' />
                    </Button>
                )}

                <input {...inputProps} />
            </div>

            <FormField name={name}>
                <TextareaInput name={name} placeholder={formatMessage({ id: `secret.${name}.placeholder` })} rows={5} id={name} />
            </FormField>

            {errorMessageId && (
                <>
                    <Spacer space={0.25} />
                    <Text elementType='span' variant='ParagraphSmallMedium' color='red' data-testid={`${name}-import-error`}>
                        <FormattedMessage id={errorMessageId} />
                    </Text>
                </>
            )}
        </div>
    );
};
